import { motion } from "framer-motion";
import { ArrowRight, Phone } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/services/api";
import logo from "../../assets/logo.svg";

/** Newsletter sign-up → POST /leads (kind "newsletter"). Was a dead input. */
function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "done" | "error">("idle");
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) return setState("error");
    setState("sending");
    try {
      await api.post("/leads", { kind: "newsletter", email: email.trim(), page: window.location.pathname });
      setState("done");
    } catch {
      setState("error");
    }
  };
  if (state === "done") return <p className="text-sm text-[#81C784]" role="status">You're on the list — thank you.</p>;
  return (
    <form onSubmit={onSubmit} className="text-gray-400" noValidate>
      <div className="flex gap-2 items-center">
        <label htmlFor="newsletter-email" className="sr-only">
          Email address
        </label>
        <input
          id="newsletter-email"
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (state === "error") setState("idle");
          }}
          placeholder="Enter your email"
          autoComplete="email"
          className="w-full p-3 rounded-md bg-gray-800 text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#4CAF50] transition-colors"
        />
        <button type="submit" disabled={state === "sending"} aria-label="Subscribe" className="bg-[#4CAF50] w-fit text-white p-3 rounded-md hover:bg-[#388e3c] transition-colors disabled:opacity-60">
          <ArrowRight size={20} />
        </button>
      </div>
      {state === "error" && <p className="mt-2 text-xs text-red-300">Enter a valid email address and try again.</p>}
    </form>
  );
}

const Footer = () => {
  return (
    <div>
      <footer className="bg-[#021403] pt-16 pb-4 text-white">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="container mx-auto px-4 md:px-16"
        >
          <div className="grid gap-12 md:grid-cols-4">
            <div>
              <div className="flex items-center gap-3">
                <img src={logo} alt="Eaze" className="w-20 h-20" />
              </div>
              <p className="mt-6 text-gray-400 text-lg">
                The all-in-one business operating system for African businesses —
                sales, stock, and real accounting in one place.
              </p>
            </div>
            <div>
              <h3 className="mb-6 text-lg font-medium">Product</h3>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <a
                    href="/#features"
                    className="hover:text-white transition-colors"
                  >
                    Features
                  </a>
                </li>
                <li>
                  <a
                    href="/#pricing"
                    className="hover:text-white transition-colors"
                  >
                    Pricing
                  </a>
                </li>
                <li>
                  <a
                    href="/#enterprise"
                    className="hover:text-white transition-colors"
                  >
                    Enterprise
                  </a>
                </li>
                <li>
                  <a
                    href="/#testimonials"
                    className="hover:text-white transition-colors"
                  >
                    Testimonials
                  </a>
                </li>
                <li>
                  <a href="/#faq" className="hover:text-white transition-colors">
                    FAQ
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="mb-6 text-lg font-medium">Contact</h3>
              <ul className="space-y-3 text-gray-400">
                <li>
                  <Link to="/contact" className="hover:text-white transition-colors">
                    Send us a message
                  </Link>
                </li>
                <li>
                  <Link to="/book-demo" className="hover:text-white transition-colors">
                    Book a demo
                  </Link>
                </li>
                <li>
                  <Link to="/enterprise" className="hover:text-white transition-colors">
                    Enterprise sales
                  </Link>
                </li>
                <li>
                  <a
                    href="tel:+233209847781"
                    className="hover:text-white transition-colors flex gap-0 items-center"
                  >
                    <Phone size={18} />
                    <span className="ml-2">+233 209 847 781</span>
                  </a>
                </li>
              </ul>
            </div>
            <div className="flex flex-col gap-3">
              <h3 className="mb-3 text-lg font-medium">Newsletter</h3>
              <p className="mb-2">Subscribe for product updates and offers.</p>
              <NewsletterForm />
            </div>
          </div>
          <div className="mt-16 border-t border-gray-800 pt-8 text-center text-gray-400 flex flex-col md:flex-row gap-3 items-center justify-between text-sm">
            <p>© 2026 Eaze · aeTech Digital Hub. All rights reserved.</p>
            <ul className="flex justify-center gap-4">
              <li>Privacy Policy</li>
              <li>Terms of Service</li>
              <li>Cookie Policy</li>
            </ul>
          </div>
        </motion.div>
      </footer>
    </div>
  );
};

export default Footer;
